import React, { useState, useEffect } from 'react';
import { useAuth } from '../../components/AuthContext';
import { db } from '../../firebase';
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  setDoc,
  deleteDoc,
  serverTimestamp,
  query,
  orderBy,
  onSnapshot, // ← los que faltan
  where,
} from 'firebase/firestore';

import { nanoid } from 'nanoid';
import { useNavigate } from 'react-router-dom';
import {
  UserCircleIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions'; // para llamar función backend
import { functions } from '../../firebase';

const AdminClients = () => {
  const { userRole, currentUser } = useAuth();
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [newClient, setNewClient] = useState({
    businessName: '',
    phone: '',
    email: '',
    address: '',
  });
  const [editingClientId, setEditingClientId] = useState(null);
  const [editData, setEditData] = useState({});
  const navigate = useNavigate();
  const [newClientMessages, setNewClientMessages] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [showNewClientModal, setShowNewClientModal] = useState(false);

  const fetchClients = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'clients'));
      const uniqueClients = {};

      snapshot.forEach(doc => {
        const data = doc.data();
        const email = (data.email || '').toLowerCase();
        if (
          !uniqueClients[email] ||
          (!uniqueClients[email].businessName && data.businessName)
        ) {
          uniqueClients[email] = { id: doc.id, ...data };
        }
      });

      const clientList = Object.values(uniqueClients);
      setClients(clientList);
      setFilteredClients(clientList);
    } catch (error) {
      console.error('Error al obtener clientes:', error);
    }
  };
  
  useEffect(() => {
    const unsubscribes = [];
  
    clients.forEach((client) => {
      if (!client?.email) return;
  
      const convoRef = collection(db, 'conversations', client.email.toLowerCase(), 'messages');
      const q = query(convoRef, orderBy('timestamp', 'desc'));
  
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const newMsgs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(msg => msg.from === 'client' && msg.read === false); // ✅ solo si no están leídos
  
        if (newMsgs.length > 0) {
          setNewClientMessages((prev) => {
            const alreadyAdded = prev.some(m => m.clientEmail === client.email);
            if (alreadyAdded) return prev;
            return [...prev, { clientEmail: client.email, body: newMsgs[0].body }];
          });
          setShowPopup(true);
        }
      });
  
      unsubscribes.push(unsubscribe);
    });
  
    return () => unsubscribes.forEach(unsub => unsub());
  }, [clients]);
  
  

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    const filtered = clients.filter((c) =>
      c.businessName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.phone?.includes(searchTerm)
    );
    setFilteredClients(filtered);
  }, [searchTerm, clients]);

  const handleInputChange = (e) => {
    setNewClient({ ...newClient, [e.target.name]: e.target.value });
  };

  const handleEditChange = (e) => {
    setEditData({ ...editData, [e.target.name]: e.target.value });
  };
  const handleDeleteClient = async (client) => {
    if (userRole === 'seller') {
      alert('No tienes permiso para eliminar clientes.');
      return;
    }
    const confirm = window.confirm(`¿Estás seguro que deseas eliminar al cliente ${client.businessName}? Esta acción no se puede deshacer.`);
    if (!confirm) return;
  
    try {
      const clientId = client.id;
      const clientEmail = client.email?.toLowerCase();
  
      // 1. Borrar subcolección de facturas
      const invoicesRef = collection(db, 'clients', clientId, 'invoices');
      const invoiceSnap = await getDocs(invoicesRef);
      const deletePromises = invoiceSnap.docs.map((docSnap) =>
        deleteDoc(docSnap.ref)
      );
      await Promise.all(deletePromises);
  
      // 2. Borrar documento principal del cliente
      await deleteDoc(doc(db, 'clients', clientId));
  
      // 3. Llamar función para borrar usuario de Firebase Auth (si tiene UID)
      if (client.uid) {
        const deleteUserFromAuth = httpsCallable(functions, 'deleteUserByUid');
        await deleteUserFromAuth({ uid: client.uid });
      }
  
      alert(' Cliente eliminado correctamente');
      await fetchClients();
    } catch (error) {
      console.error(' Error al eliminar cliente:', error);
      alert('Error al eliminar cliente');
    }
  };
  const handleCreateClient = async (e) => {
    e.preventDefault();
    try {
      const email = newClient.email.trim().toLowerCase();
      const password = Math.random().toString(36).slice(-10); // ✅ Contraseña temporal aleatoria
  
      const auth = getAuth(); // ⚠️ Asegúrate de que auth esté correctamente inicializado con tu app de Firebase
      const userCredential = await createUserWithEmailAndPassword(auth, email, password); // 🔐 Crea usuario en Auth
  
      const uid = userCredential.user.uid;
  
      // ✅ Guardar en Firestore con UID como ID
      const clientRef = doc(db, 'clients', uid);
      await setDoc(clientRef, {
        uid,
        email,
        businessName: newClient.businessName,
        phone: newClient.phone,
        address: newClient.address,
        role: 'client',
        createdAt: serverTimestamp(),
        ownerUid: currentUser?.uid || null,
        ownerEmail: currentUser?.email || null,
      });
  
      // ✅ Llamada a tu función de backend para generar y enviar el link de contraseña
      const resetLink = await fetch('https://createresetlink-xjpm53xzha-uc.a.run.app', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      }).then(res => res.json());
  
      alert('Cliente creado con éxito. Se ha enviado un correo para establecer contraseña.');
  
      // ✅ Reset del formulario
      setNewClient({ businessName: '', phone: '', email: '', address: '' });
      setShowNewClientModal(false);
      await fetchClients();
    } catch (error) {
      console.error('Error al crear cliente:', error);
  
      // ⚠️ Mejora: manejar errores específicos
      if (error.code === 'auth/email-already-in-use') {
        alert('El correo ya está registrado. Usa otro.');
      } else {
        alert('Error al crear cliente. Ver consola.');
      }
    }
  };
  
  const handleSendResetLink = async (email) => {
    if (!email) return alert('Este cliente no tiene correo.');
  
    const confirmed = window.confirm(`¿Enviar correo de acceso a ${email}?`);
    if (!confirmed) return;
  
    try {
      const response = await fetch('https://createresetlink-xjpm53xzha-uc.a.run.app', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
  
      const data = await response.json();
  
      if (response.ok) {
        alert('✅ Enlace de acceso enviado con éxito.');
      } else {
        console.error('❌ Error del backend:', data);
        alert('Error al enviar el enlace.');
      }
    } catch (err) {
      console.error('❌ Error al contactar backend:', err);
      alert('No se pudo enviar el correo. Ver consola.');
    }
  };
  

  const startEdit = (client) => {
    setEditingClientId(client.id);
    setEditData({
      businessName: client.businessName,
      phone: client.phone,
      email: client.email,
      address: client.address,
    });
  };

  const saveEdit = async (id) => {
    try {
      const ref = doc(db, 'clients', id);
      await updateDoc(ref, editData);
      alert('Cliente actualizado');
      setEditingClientId(null);
      await fetchClients();
    } catch (error) {
      console.error('Error al actualizar cliente:', error);
    }
  };

  const handleViewInvoices = (client) => {
    const clientUid = client.uid || client.id; // usa UID si existe
    navigate(`/admin/clients/${clientUid}/invoices`);
  };
  
  

  if (!clients) {
    return <p>Cargando clientes...</p>;
  }

  const statCards = [
    {
      label: 'Clientes registrados',
      value: clients.length,
      detail: 'Incluye cuentas activas y leads convertidos.',
    },
    {
      label: 'Coincidencias actuales',
      value: filteredClients.length,
      detail: 'Resultados según el filtro de búsqueda.',
    },
    {
      label: 'Mensajes sin leer',
      value: newClientMessages.length,
      detail: 'Conversaciones que requieren seguimiento.',
    },
  ];

  return (
    <div className="min-h-screen bg-[var(--admin-surface)]">
      <main className="px-4 py-8 sm:px-6 lg:px-10">
        <div className="mx-auto max-w-7xl space-y-8">
          <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {statCards.map((card) => (
              <article
                key={card.label}
                className="rounded-2xl border border-[var(--admin-border)] bg-white p-5 shadow-sm"
              >
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--admin-muted)]">
                  {card.label}
                </p>
                <p className="mt-2 text-3xl font-semibold text-[var(--admin-primary)]">
                  {card.value}
                </p>
                <p className="mt-1 text-sm text-gray-500">{card.detail}</p>
              </article>
            ))}
          </section>

          <section className="rounded-2xl border border-[var(--admin-border)] bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="text-2xl font-semibold text-[var(--admin-text)]">
                  Directorio de clientes
                </h2>
                <p className="text-sm text-[var(--admin-muted)]">
                  Puerto Rico Windows & Doors · Seguimiento de cuentas por mercado y proyecto.
                </p>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="relative w-full sm:w-72">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <svg
                      className="h-4 w-4 text-gray-500"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 20 20"
                    >
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="m19 19-4-4m0-7a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z"
                      />
                    </svg>
                  </div>
                  <input
                    type="search"
                    className="block w-full rounded-2xl border border-[var(--admin-border)] bg-white p-3 pl-10 text-sm text-gray-900 focus:border-[var(--admin-primary)] focus:ring-4 focus:ring-emerald-100"
                    placeholder="Buscar por nombre o teléfono"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setShowNewClientModal(true)}
                  className="inline-flex items-center justify-center rounded-2xl bg-[var(--admin-primary)] px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[var(--admin-primary-dark)] focus:outline-none focus:ring-4 focus:ring-emerald-100"
                >
                  <span className="mr-2 text-lg">+</span> Nuevo cliente
                </button>
              </div>
            </div>
          </section>

          {showPopup && newClientMessages.length > 0 && (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold">Mensajes no leídos</p>
                  <p className="text-xs text-emerald-700">
                    Conversaciones iniciadas desde la landing o el portal de clientes.
                  </p>
                </div>
                <button
                  onClick={() => setShowPopup(false)}
                  className="text-xs font-medium text-emerald-900 hover:underline"
                >
                  Ocultar
                </button>
              </div>
              <ul className="mt-3 space-y-2">
                {newClientMessages.map((msg, idx) => (
                  <li
                    key={`${msg.clientEmail}-${idx}`}
                    className="flex flex-wrap items-center justify-between rounded-2xl bg-white/70 px-3 py-2 text-gray-700"
                  >
                    <div>
                      <p className="text-sm font-semibold text-[var(--admin-text)]">
                        {msg.clientEmail}
                      </p>
                      <p className="text-xs text-gray-500">
                        {msg.body.slice(0, 60)}...
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        const email = encodeURIComponent(msg.clientEmail.trim().toLowerCase());
                        const qUnread = query(
                          collection(db, 'conversations', msg.clientEmail.toLowerCase(), 'messages'),
                          where('from', '==', 'client'),
                          where('read', '==', false)
                        );
                        getDocs(qUnread).then((snapshot) => {
                          snapshot.forEach((docSnap) => {
                            updateDoc(
                              doc(
                                db,
                                'conversations',
                                msg.clientEmail.toLowerCase(),
                                'messages',
                                docSnap.id
                              ),
                              { read: true }
                            );
                          });
                        });
                        navigate(`/admin/clients/${email}/email`);
                      }}
                      className="rounded-lg bg-white px-4 py-2 text-xs font-semibold text-[var(--admin-primary)] shadow-sm ring-1 ring-[var(--admin-border)] transition hover:bg-[var(--admin-primary)] hover:text-white"
                    >
                      Ver chat
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <section className="space-y-6">
            <div className="hidden lg:block">
              <div className="relative overflow-x-auto rounded-2xl border border-[var(--admin-border)] bg-white shadow-sm">
                <table className="w-full text-left text-sm text-gray-600">
                  <thead className="text-xs uppercase text-white">
                    <tr className="bg-[var(--admin-primary)]">
                      <th scope="col" className="px-6 py-3 font-semibold">
                        Cliente
                      </th>
                      <th scope="col" className="px-6 py-3 font-semibold">
                        Contacto
                      </th>
                      <th scope="col" className="px-6 py-3 font-semibold">
                        Dirección
                      </th>
                      <th scope="col" className="px-6 py-3 font-semibold text-right">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredClients.length === 0 && (
                      <tr>
                        <td
                          colSpan={4}
                          className="px-6 py-8 text-center text-sm text-gray-500"
                        >
                          No encontramos clientes con el criterio actual.
                        </td>
                      </tr>
                    )}
                    {filteredClients.map((client) => (
                      <tr
                        key={client.id}
                        className="border-b border-[var(--admin-border)] last:border-b-0"
                      >
                        <td className="px-6 py-4">
                          {editingClientId === client.id ? (
                            <input
                              className="w-full rounded-xl border border-[var(--admin-border)] px-3 py-2 text-sm focus:border-[var(--admin-primary)] focus:ring-2 focus:ring-emerald-100"
                              value={editData.businessName}
                              name="businessName"
                              onChange={(e) =>
                                setEditData({ ...editData, businessName: e.target.value })
                              }
                            />
                          ) : (
                            <div className="flex items-center gap-3">
                              <img
                                src={client.profileImageUrl || '/default-avatar.png'}
                                alt={client.businessName || client.email}
                                className="h-12 w-12 rounded-full border border-[var(--admin-border)] object-cover"
                              />
                              <div>
                                <p className="font-semibold text-[var(--admin-text)]">
                                  {client.businessName || 'Cliente sin nombre'}
                                </p>
                                <p className="text-xs text-gray-500">{client.uid || client.id}</p>
                              </div>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {editingClientId === client.id ? (
                            <div className="space-y-2">
                              <input
                                className="w-full rounded-xl border border-[var(--admin-border)] px-3 py-2 text-sm focus:border-[var(--admin-primary)] focus:ring-2 focus:ring-emerald-100"
                                value={editData.phone}
                                name="phone"
                                onChange={(e) =>
                                  setEditData({ ...editData, phone: e.target.value })
                                }
                              />
                              <input
                                className="w-full rounded-xl border border-[var(--admin-border)] px-3 py-2 text-sm focus:border-[var(--admin-primary)] focus:ring-2 focus:ring-emerald-100"
                                value={editData.email}
                                name="email"
                                onChange={(e) =>
                                  setEditData({ ...editData, email: e.target.value })
                                }
                              />
                            </div>
                          ) : (
                            <div className="space-y-1">
                              <p className="text-sm font-medium text-[var(--admin-text)]">
                                {client.phone || 'Sin teléfono'}
                              </p>
                              <p className="text-xs text-gray-500">{client.email}</p>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {editingClientId === client.id ? (
                            <input
                              className="w-full rounded-xl border border-[var(--admin-border)] px-3 py-2 text-sm focus:border-[var(--admin-primary)] focus:ring-2 focus:ring-emerald-100"
                              value={editData.address}
                              name="address"
                              onChange={(e) =>
                                setEditData({ ...editData, address: e.target.value })
                              }
                            />
                          ) : (
                            client.address || 'Sin dirección'
                          )}
                        </td>
                        <td className="px-6 py-4 text-right text-xs font-medium">
                          {editingClientId === client.id ? (
                            <div className="flex items-center justify-end gap-3">
                              <button
                                onClick={() => saveEdit(client.id)}
                                className="rounded-xl bg-[var(--admin-primary)] px-4 py-2 text-white transition hover:bg-[var(--admin-primary-dark)] focus:outline-none focus:ring-4 focus:ring-emerald-100"
                              >
                                Guardar
                              </button>
                              <button
                                onClick={() => setEditingClientId(null)}
                                className="rounded-xl border border-[var(--admin-border)] px-4 py-2 text-[var(--admin-muted)] transition hover:bg-gray-50"
                              >
                                Cancelar
                              </button>
                            </div>
                          ) : (
                            <div className="flex flex-wrap justify-end gap-2">
                              <button
                                onClick={() => startEdit(client)}
                                className="rounded-xl border border-[var(--admin-border)] px-4 py-2 text-[var(--admin-text)] hover:bg-gray-50"
                              >
                                Editar
                              </button>
                              <button
                                onClick={() => handleSendResetLink(client.email)}
                                className="rounded-xl bg-emerald-100 px-4 py-2 text-[var(--admin-primary)] hover:bg-emerald-200"
                              >
                                Credenciales
                              </button>
                              <button
                                onClick={() => handleViewInvoices(client)}
                                className="rounded-xl bg-[var(--admin-primary)] px-4 py-2 text-white hover:bg-[var(--admin-primary-dark)]"
                              >
                                Administrar
                              </button>
                              {userRole !== 'seller' && (
                                <button
                                  onClick={() => handleDeleteClient(client)}
                                  className="rounded-xl bg-red-50 px-4 py-2 text-[var(--admin-danger)] hover:bg-red-100"
                                >
                                  Eliminar
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="grid gap-4 lg:hidden">
              {filteredClients.length === 0 ? (
                <p className="text-center text-sm text-gray-500">
                  No encontramos clientes con el criterio actual.
                </p>
              ) : (
                filteredClients.map((client) => (
                  <article
                    key={`${client.id}-mobile`}
                    className="rounded-2xl border border-[var(--admin-border)] bg-white p-5 shadow-sm"
                  >
                    {editingClientId === client.id ? (
                      <div className="space-y-3">
                        <input
                          className="w-full rounded-xl border border-[var(--admin-border)] px-3 py-2 text-sm focus:border-[var(--admin-primary)] focus:ring-2 focus:ring-emerald-100"
                          value={editData.businessName}
                          name="businessName"
                          onChange={(e) =>
                            setEditData({ ...editData, businessName: e.target.value })
                          }
                        />
                        <input
                          className="w-full rounded-xl border border-[var(--admin-border)] px-3 py-2 text-sm focus:border-[var(--admin-primary)] focus:ring-2 focus:ring-emerald-100"
                          value={editData.phone}
                          name="phone"
                          onChange={(e) =>
                            setEditData({ ...editData, phone: e.target.value })
                          }
                        />
                        <input
                          className="w-full rounded-xl border border-[var(--admin-border)] px-3 py-2 text-sm focus:border-[var(--admin-primary)] focus:ring-2 focus:ring-emerald-100"
                          value={editData.email}
                          name="email"
                          onChange={(e) =>
                            setEditData({ ...editData, email: e.target.value })
                          }
                        />
                        <input
                          className="w-full rounded-xl border border-[var(--admin-border)] px-3 py-2 text-sm focus:border-[var(--admin-primary)] focus:ring-2 focus:ring-emerald-100"
                          value={editData.address}
                          name="address"
                          onChange={(e) =>
                            setEditData({ ...editData, address: e.target.value })
                          }
                        />
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => saveEdit(client.id)}
                            className="flex-1 rounded-xl bg-[var(--admin-primary)] px-4 py-2 text-white"
                          >
                            Guardar
                          </button>
                          <button
                            onClick={() => setEditingClientId(null)}
                            className="flex-1 rounded-xl border border-[var(--admin-border)] px-4 py-2 text-[var(--admin-muted)]"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-4">
                          <img
                            src={client.profileImageUrl || '/default-avatar.png'}
                            alt={client.businessName || client.email}
                            className="h-14 w-14 rounded-full border border-[var(--admin-border)] object-cover"
                          />
                          <div>
                            <p className="text-lg font-semibold text-[var(--admin-text)]">
                              {client.businessName || 'Cliente sin nombre'}
                            </p>
                            <p className="text-xs text-gray-500">{client.email}</p>
                          </div>
                        </div>
                        <dl className="mt-4 space-y-2 text-sm text-gray-600">
                          <div className="flex items-center justify-between">
                            <dt className="text-[var(--admin-muted)]">Teléfono</dt>
                            <dd className="font-semibold text-[var(--admin-text)]">
                              {client.phone || 'Sin teléfono'}
                            </dd>
                          </div>
                          <div className="flex items-center justify-between">
                            <dt className="text-[var(--admin-muted)]">Dirección</dt>
                            <dd className="text-right">{client.address || 'Sin dirección'}</dd>
                          </div>
                        </dl>
                        <div className="mt-4 flex flex-wrap gap-2">
                          <button
                            onClick={() => startEdit(client)}
                            className="flex-1 rounded-xl border border-[var(--admin-border)] px-4 py-2 text-[var(--admin-text)]"
                          >
                            Editar
                          </button>
                          <button
                            onClick={() => handleSendResetLink(client.email)}
                            className="flex-1 rounded-xl bg-emerald-100 px-4 py-2 text-[var(--admin-primary)]"
                          >
                            Credenciales
                          </button>
                          <button
                            onClick={() => handleViewInvoices(client)}
                            className="flex-1 rounded-xl bg-[var(--admin-primary)] px-4 py-2 text-white"
                          >
                            Administrar
                          </button>
                          {userRole !== 'seller' && (
                            <button
                              onClick={() => handleDeleteClient(client)}
                              className="flex-1 rounded-xl bg-red-50 px-4 py-2 text-[var(--admin-danger)]"
                            >
                              Eliminar
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </article>
                ))
              )}
            </div>
          </section>
        </div>
      </main>

      {showNewClientModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-lg rounded-3xl border border-[var(--admin-border)] bg-white p-6 shadow-2xl">
            <div className="mb-5 flex items-start justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--admin-muted)]">
                  Nuevo cliente
                </p>
                <h3 className="text-2xl font-bold text-[var(--admin-text)]">
                  Puerto Rico Windows & Doors
                </h3>
                <p className="text-sm text-gray-500">
                  Envía credenciales automáticas y registra la información básica.
                </p>
              </div>
              <button
                onClick={() => setShowNewClientModal(false)}
                className="rounded-full p-2 text-gray-400 hover:bg-gray-100"
              >
                <span className="sr-only">Cerrar</span>
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateClient} className="space-y-4">
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-3 text-gray-400">
                  <UserCircleIcon className="h-5 w-5" />
                </span>
                <input
                  name="businessName"
                  required
                  placeholder="Nombre del negocio"
                  className="w-full rounded-2xl border border-[var(--admin-border)] bg-gray-50 px-11 py-3 text-sm text-gray-900 focus:border-[var(--admin-primary)] focus:ring-4 focus:ring-emerald-100"
                  value={newClient.businessName}
                  onChange={(e) =>
                    setNewClient({ ...newClient, businessName: e.target.value })
                  }
                />
              </div>

              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-3 text-gray-400">
                  <PhoneIcon className="h-5 w-5" />
                </span>
                <input
                  name="phone"
                  required
                  placeholder="Teléfono"
                  className="w-full rounded-2xl border border-[var(--admin-border)] bg-gray-50 px-11 py-3 text-sm text-gray-900 focus:border-[var(--admin-primary)] focus:ring-4 focus:ring-emerald-100"
                  value={newClient.phone}
                  onChange={(e) =>
                    setNewClient({ ...newClient, phone: e.target.value })
                  }
                />
              </div>

              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-3 text-gray-400">
                  <EnvelopeIcon className="h-5 w-5" />
                </span>
                <input
                  name="email"
                  type="email"
                  required
                  placeholder="Correo electrónico"
                  className="w-full rounded-2xl border border-[var(--admin-border)] bg-gray-50 px-11 py-3 text-sm text-gray-900 focus:border-[var(--admin-primary)] focus:ring-4 focus:ring-emerald-100"
                  value={newClient.email}
                  onChange={(e) =>
                    setNewClient({ ...newClient, email: e.target.value })
                  }
                />
              </div>

              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-3 text-gray-400">
                  <MapPinIcon className="h-5 w-5" />
                </span>
                <input
                  name="address"
                  required
                  placeholder="Dirección del proyecto"
                  className="w-full rounded-2xl border border-[var(--admin-border)] bg-gray-50 px-11 py-3 text-sm text-gray-900 focus:border-[var(--admin-primary)] focus:ring-4 focus:ring-emerald-100"
                  value={newClient.address}
                  onChange={(e) =>
                    setNewClient({ ...newClient, address: e.target.value })
                  }
                />
              </div>

              <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => setShowNewClientModal(false)}
                  className="rounded-2xl border border-[var(--admin-border)] px-6 py-3 text-sm font-semibold text-[var(--admin-muted)] hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="rounded-2xl bg-[var(--admin-primary)] px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-[var(--admin-primary-dark)] focus:outline-none focus:ring-4 focus:ring-emerald-100"
                >
                  Crear cliente
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminClients;
